import { markRaw, nextTick } from 'vue'
import { nanoid } from 'nanoid'
import type { PageState, OcrBlock } from '@/types/ainotebook'
import { useOcrWorker } from './useOcrWorker'
import { extractPdfTextBlocksViaTextLayer } from '@/utils/ainotebook/pdfText'
import { extractBlocksFromTesseract } from '@/utils/ainotebook/ocr'
import { inflateRect } from '@/utils/ainotebook/geom'
import { fillWhite } from '@/utils/ainotebook/draw'

export function useAnalyzePage(opts: {
  pdfDoc: { value: any }
  pages: { value: PageState[] }
  ocrLang: { value: string }
  busyText: { value: string }
  selectedBlockId: { value: string }
  showMobileEditor: { value: boolean }
  renderMain: (n: number) => Promise<void>
}) {
  let analyzeToken = 0
  const { getWorkerForLang } = useOcrWorker()

  async function analyzePage(pageNumber: number) {
    const token = ++analyzeToken
    const pdfDoc = opts.pdfDoc.value
    const p = opts.pages.value.find(x => x.pageNumber === pageNumber)
    if (!pdfDoc || !p) return
    if (p.analyzing) return

    p.analyzing = true
    opts.busyText.value = '準備分析…'
    opts.selectedBlockId.value = ''

    try {
      // iOS Safari：偏向使用 PDF 文字層（避免 OCR 崩）
      const isIOSSafari =
        /iPhone|iPad|iPod/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent)

      // ensure base render at scale 2.0
      opts.busyText.value = '渲染頁面…'
      await opts.renderMain(pageNumber)

      const page = markRaw(await pdfDoc.getPage(pageNumber))
      const scale = 2.0
      const v = page.getViewport({ scale })

      // 先試 PDF 文字層
      opts.busyText.value = '擷取 PDF 文字層…'
      let blocks = await extractPdfTextBlocksViaTextLayer(page, v, p.width, p.height)
      p.usedSource = blocks.length ? 'pdf-text' : 'ocr'

      // PDF 文字層也做一點 padding（你的原始行為）
      blocks = blocks.map(b => {
        const padX = Math.max(2, b.rect.h * 0.25)
        const padY = Math.max(1, b.rect.h * 0.12)
        return { ...b, rect: inflateRect(b.rect, padX, padY, p.width, p.height) }
      })

      if (token !== analyzeToken) return

      // OCR fallback（iOS Safari 可以選擇直接跳過：這裡保留 fallback，但你可改成 return）
      if (!blocks.length && !isIOSSafari) {
        opts.busyText.value = 'OCR 辨識中…'
        const w = await getWorkerForLang(opts.ocrLang.value)

        const canvas = document.createElement('canvas')
        canvas.width = Math.floor(v.width)
        canvas.height = Math.floor(v.height)
        const ctx = canvas.getContext('2d')!
        fillWhite(ctx, canvas.width, canvas.height)
        await page.render({ canvasContext: ctx, viewport: v }).promise

        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(b => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png')
        })

        const result = await w.recognize(blob)
        blocks = extractBlocksFromTesseract(result?.data ?? result)
      }

      if (token !== analyzeToken) return

      const enriched: OcrBlock[] = blocks.map(b => ({
        id: nanoid(),
        text: b.text,
        color: '#000000',
        fontScale: 100,
        rect: b.rect,
        confidence: b.confidence,
        _origText: b.text,
        _origColor: '#000000',
        _origFontScale: 100
      }))

      p.blocks = enriched
      p.analyzed = true

      // auto focus firstHigh
      const firstHigh = enriched.find(b => (b.confidence ?? -1) >= 90) || enriched[0]
      if (firstHigh) {
        opts.selectedBlockId.value = firstHigh.id
        opts.showMobileEditor.value = true
        await nextTick()
      }
    } catch (e) {
      console.error(e)
    } finally {
      if (token === analyzeToken) {
        p.analyzing = false
        opts.busyText.value = '處理中…'
      }
    }
  }

  function cancelAnalyze() {
    analyzeToken++
  }

  return { analyzePage, cancelAnalyze }
}
